import React, { useState } from 'react';
import cx from 'classnames';

import FormattedMessage from 'components/formatted-message';
import Icon from 'components/icon';
import { PlayerSearchSuggestionDto, TeamByNameDto } from 'utils/api/dto';
import { FAV_AND_RECENT_TYPE_SCOUT_PLAYER, FAV_AND_RECENT_TYPE_TEAM } from 'widgets/favorites/constant';

import { PlayerRow, RowFavorite, TeamRow } from '../dropdown-row';
import FilterPills, { SearchFilter } from '../filter-pills';
import styles from '../search-dropdown.module.css';
import { FavoriteItemType, FavoriteToggleHandler, SelectItemHandler } from '../types';

type ResultsSectionProps = {
    query: string;
    players?: PlayerSearchSuggestionDto[] | null;
    teams?: TeamByNameDto[] | null;
    processing: boolean;
    /** the search request failed: the lists are empty because of it */
    failed?: boolean;
    getPhotoUrl: (player: PlayerSearchSuggestionDto) => string | null;
    /** the id to pass to onFavoriteToggle when the item is a favorite; null: the stars are not shown */
    getFavoriteId: ((id: number, type: FavoriteItemType) => number | undefined) | null;
    onSelect: SelectItemHandler;
    onFavoriteToggle: FavoriteToggleHandler;
};

const ResultsSection = ({
    query,
    players,
    teams,
    processing,
    failed = false,
    getPhotoUrl,
    getFavoriteId,
    onSelect,
    onFavoriteToggle
}: ResultsSectionProps) => {
    const [filter, setFilter] = useState<SearchFilter>('all');
    const foundPlayers = players ?? [];
    const foundTeams = teams ?? [];
    const showPlayers = filter !== 'teams';
    const showTeams = filter !== 'players';

    const getFavorite = (id: number, type: FavoriteItemType): RowFavorite | undefined => {
        if (!getFavoriteId) {
            return undefined;
        }
        const favoriteId = getFavoriteId(id, type);

        return { isActive: !!favoriteId, onToggle: () => onFavoriteToggle(favoriteId, { id, type }) };
    };

    return (
        <div className={cx(styles.section, styles.resultsSection)}>
            <FilterPills
                value={filter}
                onChange={setFilter}
                counts={{
                    all: foundPlayers.length + foundTeams.length,
                    players: foundPlayers.length,
                    teams: foundTeams.length
                }}
            />
            {processing ? (
                <div className={styles.loader}>
                    <Icon type="spinner" pulse />
                </div>
            ) : (
                <div className={styles.list}>
                    {showPlayers
                        ? foundPlayers.map(player => (
                              <PlayerRow
                                  key={`player-${player.playerProfileId}`}
                                  player={player}
                                  photoUrl={getPhotoUrl(player)}
                                  meta={player.lastTeam}
                                  favorite={getFavorite(player.playerProfileId, FAV_AND_RECENT_TYPE_SCOUT_PLAYER)}
                                  onClick={() => onSelect(player.playerProfileId, FAV_AND_RECENT_TYPE_SCOUT_PLAYER)}
                              />
                          ))
                        : null}
                    {showTeams
                        ? foundTeams.map(team => (
                              <TeamRow
                                  key={`team-${team.teamId}`}
                                  name={team.nameEn}
                                  logo={team.logoHref}
                                  meta={team.acronymEn}
                                  favorite={getFavorite(team.teamId, FAV_AND_RECENT_TYPE_TEAM)}
                                  onClick={() => onSelect(team.teamId, FAV_AND_RECENT_TYPE_TEAM)}
                              />
                          ))
                        : null}
                    {failed ? (
                        <div className={styles.empty}>
                            <FormattedMessage id="searchbar.searchFailed" />
                        </div>
                    ) : null}
                    {!failed && showPlayers && !foundPlayers.length ? (
                        <div className={styles.empty}>
                            <FormattedMessage id="searchbar.noPlayersMatched" values={{ query }} />
                        </div>
                    ) : null}
                    {!failed && showTeams && !foundTeams.length ? (
                        <div className={styles.empty}>
                            <FormattedMessage id="searchbar.noTeamsMatched" values={{ query }} />
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default ResultsSection;
